import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { auth } from '../lib/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { getUserCredit, createCheckoutSession } from '../lib/creditService';

const PurchaseCredits = () => {
  const navigate = useNavigate();
  const [currentCredits, setCurrentCredits] = useState(0);
  const [creditsToBuy, setCreditsToBuy] = useState(5);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');

  const MINIMUM_CREDITS = 5;
  const MAXIMUM_CREDITS = 5000;
  const PRICE_PER_CREDIT = 0.20;
  const DISCOUNT_THRESHOLD = 100;
  const DISCOUNT_PERCENT = 20;

  const hasDiscount = creditsToBuy >= DISCOUNT_THRESHOLD;
  const discountMultiplier = hasDiscount ? (1 - DISCOUNT_PERCENT / 100) : 1;
  const originalPrice = (creditsToBuy && !isNaN(creditsToBuy) ? (creditsToBuy * PRICE_PER_CREDIT).toFixed(2) : '0.00');
  const totalPrice = (creditsToBuy && !isNaN(creditsToBuy) ? (creditsToBuy * PRICE_PER_CREDIT * discountMultiplier).toFixed(2) : '0.00');

  const checkUser = useCallback(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) navigate('/login');
    });
    return unsubscribe;
  }, [navigate]);

  const loadCurrentCredits = useCallback(async () => {
    try {
      const balance = await getUserCredit();
      if (balance.success) setCurrentCredits(balance.credit);
    } catch (error) {
      console.error('Error loading credits:', error);
    }
  }, []);

  useEffect(() => {
    const unsub = checkUser();
    loadCurrentCredits();
    return () => unsub();
  }, [checkUser, loadCurrentCredits]);

  const validateCredits = (value) => {
    if (value === '' || value === null || value === undefined) return 'Please enter a number of credits';
    const numValue = Number(value);
    if (isNaN(numValue)) return 'Please enter a valid number';
    if (!Number.isInteger(numValue)) return 'Only whole numbers are allowed';
    if (numValue < MINIMUM_CREDITS) return `Minimum purchase is ${MINIMUM_CREDITS} credits`;
    if (numValue > MAXIMUM_CREDITS) return `Maximum purchase is ${MAXIMUM_CREDITS} credits`;
    return '';
  };

  const handleCreditsChange = (e) => {
    const value = e.target.value;
    if (value === '') { setCreditsToBuy(''); setValidationError('Please enter a number of credits'); return; }
    const numericValue = value.replace(/[^0-9]/g, '');
    if (numericValue !== value) return;
    const intValue = parseInt(numericValue) || 0;
    setCreditsToBuy(intValue);
    setValidationError(validateCredits(intValue));
  };

  const handlePurchase = async () => {
    const validation = validateCredits(creditsToBuy);
    if (validation) { setValidationError(validation); return; }
    setIsLoading(true);
    setError('');
    try {
      const result = await createCheckoutSession(creditsToBuy);
      if (result.success && result.url) {
        window.location.href = result.url;
        return;
      } else {
        setError(result.error || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      setError('Purchase failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const benefits = [
    { icon: '📊', title: 'Prioritized Insights', description: 'Get clear issue ranking, sentiment trends, and actionable summaries' },
    { icon: '📄', title: 'Export-Ready Reports', description: 'Download professional reports for stakeholders and roadmap planning' },
    { icon: '♻️', title: 'Unlimited Re-Access', description: 'Re-open completed analyses whenever you need' },
    { icon: '🎯', title: 'Simple Usage Model', description: '1 credit = 1 new app analysis' },
    { icon: '⚡', title: 'Smart Caching', description: 'Recent analyses are cached for 24h with no extra credit charge' },
    { icon: '🔄', title: 'Always Up-to-Date', description: 'Run fresh analyses when new reviews start changing sentiment' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Helmet>
        <title>Buy Analysis Credits | AppReview.ai Pricing</title>
        <meta name="description" content="Purchase analysis credits for AppReview.ai. Affordable pay-as-you-go pricing for AI-powered app store review analysis — no subscription required." />
        <link rel="canonical" href="https://appreview.ai/purchase-credits" />
        <meta property="og:url" content="https://appreview.ai/purchase-credits" />
        <meta property="og:title" content="AppReview.ai Pricing — Pay-As-You-Go Analysis Credits" />
        <meta property="og:description" content="Affordable AI-powered app review analysis. Buy credits as you need them — no subscription lock-in." />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index,follow" />
      </Helmet>
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/')} className="text-gray-600 hover:text-gray-900 flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              <span>Back to Dashboard</span>
            </button>
            <div className="text-sm text-gray-600">Current Balance: <span className="font-semibold text-blue-600">{currentCredits} credits</span></div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Plan</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">Start with flexible credits and scale as your review analysis workflow grows.</p>
        </div>

        {error && (
          <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="max-w-2xl mx-auto mb-16">
          <div className="bg-white rounded-2xl shadow-xl border-2 border-blue-200 p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Flexible Credit Pricing</h2>
              <p className="text-gray-600">1 credit = 1 new app analysis. Reuse cached analyses at no extra cost.</p>
            </div>
            <div className="space-y-6">
              <div>
                <label htmlFor="credits" className="block text-sm font-semibold text-gray-700 mb-3">Number of Credits</label>
                <input type="number" id="credits" min={MINIMUM_CREDITS} max={MAXIMUM_CREDITS} step="1" value={creditsToBuy || ''} onChange={handleCreditsChange} placeholder={`Enter ${MINIMUM_CREDITS} - ${MAXIMUM_CREDITS} credits`} className="w-full px-4 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all duration-200" disabled={isLoading} />
                {validationError && <p className="mt-2 text-sm text-red-600">{validationError}</p>}
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[25, 50, 100, 250].map((pack) => (
                    <button
                      key={pack}
                      type="button"
                      onClick={() => { setCreditsToBuy(pack); setValidationError(validateCredits(pack)); }}
                      className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${creditsToBuy === pack ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300 text-gray-700'}`}
                    >
                      {pack} credits
                    </button>
                  ))}
                </div>
              </div>
              {hasDiscount && (
                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 text-center">
                  <span className="text-green-700 font-bold text-lg">🎉 {DISCOUNT_PERCENT}% OFF applied!</span>
                  <span className="text-green-600 text-sm ml-2">Buy {DISCOUNT_THRESHOLD}+ credits and save</span>
                </div>
              )}
              {!hasDiscount && creditsToBuy > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                  <span className="text-amber-700 text-sm">💡 Buy {DISCOUNT_THRESHOLD}+ credits to get <strong>{DISCOUNT_PERCENT}% OFF</strong></span>
                </div>
              )}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                  <div>
                    {hasDiscount && <div className="text-sm text-gray-400 line-through">${originalPrice}</div>}
                    <div className="text-2xl font-bold text-blue-600">${totalPrice}</div>
                    <div className="text-sm text-gray-600">Total Cost</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">${(PRICE_PER_CREDIT * discountMultiplier).toFixed(2)}</div>
                    <div className="text-sm text-gray-600">Per Credit</div>
                  </div>
                  <div><div className="text-2xl font-bold text-purple-600">{(currentCredits + (creditsToBuy || 0)).toLocaleString()}</div><div className="text-sm text-gray-600">Total After Purchase</div></div>
                </div>
              </div>
              <button onClick={handlePurchase} disabled={isLoading || !!validationError || !creditsToBuy || creditsToBuy < MINIMUM_CREDITS} className={`w-full py-4 px-8 rounded-xl font-bold text-lg transition-all duration-200 ${isLoading || !!validationError || !creditsToBuy || creditsToBuy < MINIMUM_CREDITS ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl'}`}>
                {isLoading ? 'Processing Payment...' : `Purchase ${creditsToBuy || 0} Credits for $${totalPrice}`}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What’s Included</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((b, i) => (
              <div key={i} className="text-center p-6">
                <div className="text-4xl mb-4">{b.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{b.title}</h3>
                <p className="text-gray-600 leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseCredits;
